import React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Popper,
    Paper,
    Stack,
    Button,
    Badge,
    CircularProgress,
    OutlinedInput,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { GrTable } from 'react-icons/gr';
import { FaChartBar } from 'react-icons/fa';
import { SiMicrosoftexcel } from 'react-icons/si';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import { IpamLookups } from 'api/ipam-lookups';
import { saveAs } from 'file-saver';
import filterIcon from 'assets/images/filter-icon.svg';
import ipamAdminStyle from 'pages/ipam-admin/style';
import {
    ipamAdminSectionState,
    ipamAdminSectionOptionsState,
    ipamAdminViewModeState,
    ipamAdminPendingFiltersState,
    ipamAdminAppliedFiltersState,
    ipamAdminTypeOptionsState,
    ipamAdminProviderOptionsState,
    ipamAdminContinentOptionsState,
    ipamAdminSearchTextState,
    ipamAdminAddLabelState,
    ipamAdminAddTriggerState,
    IPAM_FILTER_DEFAULT,
} from 'pages/ipam-admin/store';

const SECTION_FILTER_CONFIG = {
    regions: { showProvider: true, showRegion: true, showStatus: false, showContinent: false, showType: false },
    'super-supernet': { showProvider: false, showRegion: false, showStatus: true, showContinent: true, showType: false },
    supernet: { showProvider: true, showRegion: true, showStatus: false, showContinent: false, showType: true },
    hub: { showProvider: true, showRegion: true, showStatus: false, showContinent: false, showType: false },
    allocation: { showProvider: true, showRegion: true, showStatus: true, showContinent: false, showType: false },
};

const IpamAdminHeader = () => {
    const [section, setSection] = useRecoilState(ipamAdminSectionState);
    const sectionOptions = useRecoilValue(ipamAdminSectionOptionsState);
    const [viewMode, setViewMode] = useRecoilState(ipamAdminViewModeState);
    const [pendingFilters, setPendingFilters] = useRecoilState(ipamAdminPendingFiltersState);
    const [appliedFilters, setAppliedFilters] = useRecoilState(ipamAdminAppliedFiltersState);
    const typeOptions = useRecoilValue(ipamAdminTypeOptionsState);
    const setTypeOptions = useSetRecoilState(ipamAdminTypeOptionsState);
    const setProviderOptionsStore = useSetRecoilState(ipamAdminProviderOptionsState);
    const setContinentOptionsStore = useSetRecoilState(ipamAdminContinentOptionsState);
    const [searchText, setSearchText] = useRecoilState(ipamAdminSearchTextState);
    const addLabel = useRecoilValue(ipamAdminAddLabelState);
    const setAddTrigger = useSetRecoilState(ipamAdminAddTriggerState);

    const [localSearch, setLocalSearch] = React.useState('');
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [providerOptions, setProviderOptions] = React.useState([{ value: '', label: 'All Providers' }]);
    const [regionOptions, setRegionOptions] = React.useState([{ value: '', label: 'All Regions' }]);
    const [continentOptions, setContinentOptions] = React.useState([{ value: '', label: 'All Continents' }]);
    const [statusOptions, setStatusOptions] = React.useState([{ value: '', label: 'All Status' }]);

    const config = SECTION_FILTER_CONFIG[section] || {};
    const open = Boolean(anchorEl);

    const cloudProvidersApi = useMutation(() => axios.get(endPoints.landingZones.cloudProvider));
    const regionsApi = useMutation((provider) => axios.get(IpamLookups.regionFilters(provider)));
    const statusApi = useMutation(() => axios.get(IpamLookups.statusValues));
    const continentsApi = useMutation(() => axios.get(IpamLookups.superSupernetContinents));
    const routabilityApi = useMutation(() => axios.get(IpamLookups.networkRoutability));
    const exportApi = useMutation((body) => axios.post(IpamLookups.exportAllocations, body), {
        onSuccess: (resp) => {
            if (resp) {
                const blob = new Blob([resp], { type: 'application/csv' });
                const file = new File([blob], 'ipam-allocation.csv', { type: 'application/csv' });
                saveAs(file);
            }
        },
    });

    const handleExport = () => {
        exportApi.mutate({
            searchKey: '',
            searchColumns: [],
            pageNumber: 1,
            pageSize: 10000,
            filterBy: {
                provider: appliedFilters.provider,
                regionId: appliedFilters.region,
                status: appliedFilters.status,
            },
        });
    };

    const handleFilterToggle = (event) => {
        if (!anchorEl) {
            if (config.showProvider) cloudProvidersApi.mutate();
            if (config.showRegion) regionsApi.mutate(pendingFilters.provider);
            if (config.showStatus) statusApi.mutate();
            if (config.showContinent) continentsApi.mutate();
            if (config.showType) routabilityApi.mutate();
        }
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    React.useEffect(() => {
        if (cloudProvidersApi.data) {
            const ALLOWED_PROVIDERS = ['aws', 'azure'];
            const providers = (cloudProvidersApi.data?.data || []).filter((p) => ALLOWED_PROVIDERS.includes(p.name?.toLowerCase()));
            setProviderOptions([
                { value: '', label: 'All Providers' },
                ...providers.map((p) => ({ value: p.name?.toLowerCase() ?? '', label: p.name?.toUpperCase() ?? '' })),
            ]);
            setProviderOptionsStore(providers.map((p) => p.name?.toLowerCase()).filter(Boolean));
        }
    }, [cloudProvidersApi.data]);

    React.useEffect(() => {
        if (regionsApi.data) {
            const regions = regionsApi.data?.data || [];
            const seen = new Set();
            const uniqueRegions = regions
                .filter((r) => {
                    const id = r?.regionId || '';
                    if (!id || seen.has(id)) return false;
                    seen.add(id);
                    return true;
                })
                .sort((a, b) => (a?.regionId || '').localeCompare(b?.regionId || ''));
            setRegionOptions([{ value: '', label: 'All Regions' }, ...uniqueRegions.map((r) => ({ value: r?.regionId, label: r?.region || r?.regionId }))]);
        }
    }, [regionsApi.data]);

    React.useEffect(() => {
        if (continentsApi.data) {
            const continents = continentsApi.data?.data || [];
            setContinentOptions([{ value: '', label: 'All Continents' }, ...continents.map((c) => ({ value: c, label: c }))]);
            setContinentOptionsStore(continents);
        }
    }, [continentsApi.data]);

    React.useEffect(() => {
        if (routabilityApi.data) setTypeOptions(routabilityApi.data?.data || []);
    }, [routabilityApi.data]);

    React.useEffect(() => {
        if (statusApi.data) {
            const data = statusApi.data?.data || {};
            if (section === 'allocation') {
                const statuses = data.allocation || [];
                setStatusOptions([{ value: '', label: 'All Status' }, ...statuses.map((s) => ({ value: s, label: s }))]);
            } else if (section === 'super-supernet') {
                const statuses = data['super-supernet'] || [];
                setStatusOptions([{ value: '', label: 'All Status' }, ...statuses.map((s) => ({ value: s, label: s?.toUpperCase() }))]);
            }
        }
    }, [statusApi.data, section]);

    const handleProviderChange = (val) => {
        setPendingFilters((prev) => ({ ...prev, provider: val, region: '', regionName: '' }));
        if (config.showRegion) regionsApi.mutate(val);
    };

    const activeFilterCount = Object.values(appliedFilters).filter((v) => Boolean(v)).length;

    const handleApply = () => {
        setAppliedFilters({ ...pendingFilters });
        setAnchorEl(null);
    };

    const handleReset = () => {
        const cleared = { ...IPAM_FILTER_DEFAULT };
        setPendingFilters(cleared);
        setAppliedFilters(cleared);
        setAnchorEl(null);
    };

    const fullTypeOptions = React.useMemo(() => [{ value: '', label: 'All Types' }, ...typeOptions.map((t) => ({ value: t, label: t }))], [typeOptions]);

    // Sync local search input when store value changes (e.g. cleared on section change)
    React.useEffect(() => {
        setLocalSearch(searchText);
    }, [searchText]);

    const handleSearchCommit = () => setSearchText(localSearch);
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') setSearchText(localSearch);
    };
    const handleSearchClear = () => {
        setLocalSearch('');
        setSearchText('');
    };

    return (
        <Box sx={ipamAdminStyle.pageHeader}>
            <Typography sx={ipamAdminStyle.pageTitle}>IPAM Admin Portal</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Search */}
                <OutlinedInput
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search..."
                    size="small"
                    sx={ipamAdminStyle.searchBox}
                    endAdornment={
                        <InputAdornment position="end">
                            {localSearch ? (
                                <IconButton size="small" onClick={handleSearchClear} sx={ipamAdminStyle.searchIconBtn}>
                                    <CloseIcon sx={ipamAdminStyle.searchClearIcon} />
                                </IconButton>
                            ) : (
                                <IconButton size="small" onClick={handleSearchCommit} sx={ipamAdminStyle.searchIconBtn}>
                                    <SearchIcon sx={ipamAdminStyle.searchIcon} />
                                </IconButton>
                            )}
                        </InputAdornment>
                    }
                />
                {/* Add Button */}
                {addLabel && (
                    <Button variant="outlined" onClick={() => setAddTrigger((n) => n + 1)} sx={ipamAdminStyle.addBtn}>
                        {addLabel}
                    </Button>
                )}
                {/* Filter Button */}
                <Button
                    sx={ipamAdminStyle.filterBtn}
                    startIcon={
                        <Badge variant="dot" overlap="circular" color={activeFilterCount > 0 ? 'error' : 'default'}>
                            <img src={filterIcon} alt="filter icon" />
                        </Badge>
                    }
                    disableElevation
                    disableFocusRipple
                    disableRipple
                    onClick={handleFilterToggle}
                >
                    Filter
                </Button>

                {/* Filter Popper */}
                <Popper open={open} anchorEl={anchorEl} style={{ zIndex: 1300 }} placement="bottom-end">
                    <Paper elevation={5} sx={ipamAdminStyle.filterPopper.wrapper}>
                        <ArrowDropUpIcon sx={ipamAdminStyle.filterPopper.arrow} />
                        <Box sx={ipamAdminStyle.filterPopper.closeRow}>
                            <CloseIcon fontSize="small" sx={ipamAdminStyle.filterPopper.closeIcon} onClick={() => setAnchorEl(null)} />
                        </Box>
                        <Stack spacing={2} sx={{ mb: 2 }}>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={section}
                                    onChange={(e) => {
                                        const newSection = e.target.value;
                                        const newConfig = SECTION_FILTER_CONFIG[newSection] || {};
                                        setSection(newSection);
                                        setViewMode('table');
                                        setPendingFilters({ ...IPAM_FILTER_DEFAULT });
                                        setAppliedFilters({ ...IPAM_FILTER_DEFAULT });
                                        setTypeOptions([]);
                                        setSearchText('');
                                        setAddTrigger(0);
                                        if (newConfig.showProvider) cloudProvidersApi.mutate();
                                        if (newConfig.showRegion) regionsApi.mutate('');
                                        if (newConfig.showStatus) statusApi.mutate();
                                        if (newConfig.showContinent) continentsApi.mutate();
                                        if (newConfig.showType) routabilityApi.mutate();
                                    }}
                                    displayEmpty
                                >
                                    {sectionOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {config.showProvider && (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={pendingFilters.provider}
                                        onChange={(e) => handleProviderChange(e.target.value)}
                                        displayEmpty
                                        IconComponent={cloudProvidersApi.isLoading ? () => <CircularProgress size={14} sx={{ mr: 1 }} /> : ArrowDropDownIcon}
                                    >
                                        {providerOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {config.showRegion && (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={pendingFilters.region}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const opt = regionOptions.find((o) => o.value === val);
                                            setPendingFilters((prev) => ({ ...prev, region: val, regionName: val ? opt?.label || val : '' }));
                                        }}
                                        displayEmpty
                                        IconComponent={regionsApi.isLoading ? () => <CircularProgress size={14} sx={{ mr: 1 }} /> : ArrowDropDownIcon}
                                    >
                                        {regionOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {config.showContinent && (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={pendingFilters.continent}
                                        onChange={(e) => setPendingFilters((prev) => ({ ...prev, continent: e.target.value }))}
                                        displayEmpty
                                        IconComponent={continentsApi.isLoading ? () => <CircularProgress size={14} sx={{ mr: 1 }} /> : ArrowDropDownIcon}
                                    >
                                        {continentOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {config.showStatus && (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={pendingFilters.status}
                                        onChange={(e) => setPendingFilters((prev) => ({ ...prev, status: e.target.value }))}
                                        displayEmpty
                                        IconComponent={statusApi.isLoading ? () => <CircularProgress size={14} sx={{ mr: 1 }} /> : ArrowDropDownIcon}
                                    >
                                        {statusOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {config.showType && (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={pendingFilters.type}
                                        onChange={(e) => setPendingFilters((prev) => ({ ...prev, type: e.target.value }))}
                                        displayEmpty
                                    >
                                        {fullTypeOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Stack>
                        <Stack direction="row" justifyContent="center" spacing={2}>
                            <Button variant="text" onClick={handleReset} sx={ipamAdminStyle.filterPopper.resetBtn}>
                                Reset
                            </Button>
                            <Button variant="contained" onClick={handleApply} sx={ipamAdminStyle.filterPopper.applyBtn}>
                                Apply
                            </Button>
                        </Stack>
                    </Paper>
                </Popper>

                {section === 'allocation' && (
                    <>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(_, val) => {
                                if (val) setViewMode(val);
                            }}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    border: '1px solid #ccc',
                                    color: '#9e9e9e',
                                    padding: '4px 8px',
                                },
                                '& .Mui-selected': {
                                    backgroundColor: '#0047BA',
                                    color: '#fff',
                                    border: '1px solid #0047BA',
                                },
                                '& .Mui-selected:hover': {
                                    backgroundColor: '#003d8c',
                                    color: '#fff',
                                },
                            }}
                        >
                            <Tooltip title="Table View">
                                <ToggleButton value="table" aria-label="table view">
                                    <GrTable size={16} />
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title="Widget View">
                                <ToggleButton value="widget" aria-label="widget view">
                                    <FaChartBar size={16} />
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                        <Tooltip title="Export CSV">
                            <Box
                                sx={{
                                    background: '#0047BA',
                                    width: '37px',
                                    height: '37px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                    cursor: exportApi.isLoading ? 'not-allowed' : 'pointer',
                                    opacity: exportApi.isLoading ? 0.7 : 1,
                                }}
                                onClick={!exportApi.isLoading ? handleExport : undefined}
                            >
                                {exportApi.isLoading ? <CircularProgress color="inherit" size={20} /> : <SiMicrosoftexcel color="#ffffff" size={20} />}
                            </Box>
                        </Tooltip>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default IpamAdminHeader;
